import type { ReleaseDocuments } from "../DocumentUtils";
import { getNamespaceForDefinedIn, getNodeKey, normalizeText } from "./GraphMisc";
import { cacheGraphNodeLookup, getCachedGraphNode, iterateGraphNodeValues } from "./GraphTraversal";
import type { GraphNode, RuleGraph } from "./GraphTypes";

const NORMALIZED_DESCRIPTION = Symbol("NormalizedDescription");
const NORMALIZED_PUBLIC_ID = Symbol("NormalizedPublicId");

const createNodeCacheKey = (descriptionOrId: string, currentNamespace?: string): string => {
  return `${descriptionOrId.toLowerCase()}///${currentNamespace ? `:${currentNamespace.toLowerCase()}` : ""}`;
};

const normalizeDescription = <S extends string | undefined>(text: S): S => {
  if (!text) {
    return text;
  }
  return normalizeText(text).toLowerCase() as S;
};

const getNormalizedDescription = (node: GraphNode): string | undefined => {
  // @ts-ignore
  if (node[NORMALIZED_DESCRIPTION] !== undefined) {
    // @ts-ignore
    return node[NORMALIZED_DESCRIPTION];
  }
  const normalized = normalizeDescription(node.description);
  if (process.env.NODE_ENV !== "test") {
    // @ts-ignore
    node[NORMALIZED_DESCRIPTION] = normalized;
  }
  return normalized;
};

const getNormalizedPublicId = (node: GraphNode): string | undefined => {
  // @ts-ignore
  if (node[NORMALIZED_PUBLIC_ID] !== undefined) {
    // @ts-ignore
    return node[NORMALIZED_PUBLIC_ID];
  }
  const normalized = normalizeDescription(node.publicId);
  if (process.env.NODE_ENV !== "test") {
    // @ts-ignore
    node[NORMALIZED_PUBLIC_ID] = normalized;
  }
  return normalized;
};

const findClosestFallbackNode = (
  nodes: GraphNode[],
  currentNamespace: string | undefined,
  documents: ReleaseDocuments,
): GraphNode | undefined => {
  let bestNode: GraphNode | undefined;
  let bestIndex = Number.POSITIVE_INFINITY;

  for (const node of nodes) {
    const index = getNamespaceForDefinedIn(node.definedIn, documents)?.indexOf(currentNamespace || "");
    if (index === -1 || !index) {
      continue;
    }
    if (index < bestIndex) {
      bestIndex = index;
      bestNode = node;
    }
  }

  return bestNode ?? nodes[0];
};

export const findNode = (
  graph: RuleGraph,
  documents: ReleaseDocuments | undefined,
  descriptionOrId: string | undefined,
  currentNamespace?: string,
): GraphNode | undefined => {
  if (!descriptionOrId) {
    return undefined;
  }

  descriptionOrId = normalizeDescription(descriptionOrId);
  const noDocumentsCacheKey = createNodeCacheKey(descriptionOrId, "__no_documents__");
  const cachedWithoutDocuments = getCachedGraphNode(graph, noDocumentsCacheKey);
  if (cachedWithoutDocuments) {
    return cachedWithoutDocuments;
  }

  if (!documents) {
    for (const node of iterateGraphNodeValues(graph)) {
      if (
        node.id === descriptionOrId ||
        getNormalizedDescription(node) === descriptionOrId ||
        getNormalizedPublicId(node) === descriptionOrId
      ) {
        cacheGraphNodeLookup(graph, noDocumentsCacheKey, node);
        return node;
      }
    }
    return undefined;
  }
  currentNamespace = getNamespaceForDefinedIn(currentNamespace, documents);
  const cacheKey = createNodeCacheKey(descriptionOrId, currentNamespace);
  const cached = getCachedGraphNode(graph, cacheKey);
  if (cached) {
    return cached;
  }

  if (descriptionOrId[0] === "/") {
    // They have included scoping to determine the correct path
    // We assume this is a full path
    const els = descriptionOrId.split("/");
    // get rid of leading empty '' due to starting /
    const _desc = els.pop(); // We assume that they can't have / in the description.
    const namespace = els.join("/");
    for (const node of iterateGraphNodeValues(graph)) {
      // we also accept nodes without a "definedIn", so we can transform input nodes into derived nodes
      if (
        (node.id === descriptionOrId ||
          getNormalizedDescription(node) === _desc ||
          getNormalizedPublicId(node) === descriptionOrId) &&
        (!node.definedIn || normalizeDescription(getNamespaceForDefinedIn(node.definedIn, documents)) === namespace)
      ) {
        cacheGraphNodeLookup(graph, cacheKey, node);
        return node;
      }
    }
  }
  // Otherwise we have to find using scoping
  const parts = descriptionOrId.split("/");
  const key = parts.pop();
  const entityPath = parts.join("/") || undefined;
  const nodes: GraphNode[] = [];
  const fallbackNodes: GraphNode[] = [];
  for (const node of iterateGraphNodeValues(graph)) {
    if (!node) {
      continue;
    }
    if (node.path === descriptionOrId) {
      nodes.push(node);
      continue;
    }

    const keyMatches =
      node.id?.toLowerCase() === key || getNormalizedPublicId(node) === key || getNormalizedDescription(node) === key;
    if (keyMatches) {
      if (!entityPath) {
        nodes.push(node);
        continue;
      }
      const nodeKey = getNodeKey(node, documents);
      if (entityPath === nodeKey.entityPath) {
        nodes.push(node);
        continue;
      }
    }

    if (
      node.id === descriptionOrId ||
      getNormalizedDescription(node) === descriptionOrId ||
      getNormalizedPublicId(node) === descriptionOrId
    ) {
      fallbackNodes.push(node);
    }
  }

  if (!nodes?.length) {
    // but maybe they have / in the description name, naughty, but older documents do it

    // sort by closest to namespace
    if (fallbackNodes.length) {
      const node = findClosestFallbackNode(fallbackNodes, currentNamespace, documents);
      cacheGraphNodeLookup(graph, cacheKey, node);
      return node;
    }

    return undefined;
  }
  if (nodes.length === 1) {
    // There is only one node - great use that
    const node = nodes[0];
    cacheGraphNodeLookup(graph, cacheKey, node);
    return node;
  }
  // if we don't know what namespace we are from, just take the first
  if (!currentNamespace) {
    const node = nodes[0];
    cacheGraphNodeLookup(graph, cacheKey, node);
    return node;
  }
  // There are multiple nodes that match this description. We need to determine which one is the most likely scoped attribute
  // To do that we find the one with the closest namespace/definedIn scope
  const currentPath = currentNamespace.split("/");
  const matches: GraphNode[] = [];
  for (const n of nodes) {
    if (!n.definedIn) {
      continue;
      // This should never get here. If the input node existed it would have been picked up in the find above or if it didn't exist it would not return in the find so the false above would trigger it's creation. SHould catch here just to prevent the below code firing accidentally
    }
    const path = getNamespaceForDefinedIn(n.definedIn, documents)?.split("/") || [];
    // Now compare this with the provided namespace. We are trying to find the node that has the 'closest' path to the current namespace
    let matchCount = 0;
    for (let i = 0; i < path.length; i++) {
      const p = path[i];
      if (p === currentPath[i]) matchCount++;
      else {
        // As soon as we don't match stop counting
        break;
      }
    }
    // We record this in the matches at the index of how much it matches
    // If it doesn't match (eg: 0) then we can't link to this one - it's in the wrong chain. They should have been more specific
    if (matchCount > 0) matches[matchCount] = n;
  }
  if (matches.length === 0) return undefined;
  const node = matches.pop(); // Return the closest match
  if (node) {
    cacheGraphNodeLookup(graph, cacheKey, node);
  }
  return node;
};

/*export const extractEntityKey = (string: string): EntityKey | undefined => {

};*/
